import { DebouncedFunction, LocalUserSettings, ModelRow, WindowTitle } from "@mcleod/core";
import { Component, Layout, SplitterOrientation } from "@mcleod/components";
import { OrderPlanningTimeline } from "../timeline/OrderPlanningTimeline";
import { AutogenLayoutOrderPlanningSplitView } from "./autogen/AutogenLayoutOrderPlanningSplitView";
import { OperationsManagementMultiProfile } from "./OperationsManagementMultiProfile";
import { OrderPlanningEquipment } from "./OrderPlanningEquipment";
import { Tools } from "../OrderPlanningToolPanel";

export interface OrderPlanningSplitViewDisplayMode {
    name: string;
    dbValue: string;
    splitterOrientation: SplitterOrientation;
    layout1: string;
    layout2: string;
}

export class OrderPlanningSplitView extends AutogenLayoutOrderPlanningSplitView {
    private static LOCAL_SETTINGS_SPLITTER_KEY = "lme/dispatch/planning/opsmgmt/OrderPlanningSplitView.splitter";
    public static readonly ORDERS_EQUIPMENT_HORIZONTAL: OrderPlanningSplitViewDisplayMode = {
        name: "ordersEquipmentHorizontal",
        dbValue: "V",
        splitterOrientation: SplitterOrientation.HORIZONTAL,
        layout1: "orders",
        layout2: "equipment"
    };
    public static readonly ORDERS_EQUIPMENT_VERTICAL: OrderPlanningSplitViewDisplayMode = {
        name: "ordersEquipmentVertical",
        dbValue: "H",
        splitterOrientation: SplitterOrientation.VERTICAL,
        layout1: "orders",
        layout2: "equipment"
    };
    public static readonly ORDERS_TIMELINE_VERTICAL: OrderPlanningSplitViewDisplayMode = {
        name: "ordersTimelineVertical",
        dbValue: "C",
        splitterOrientation: SplitterOrientation.VERTICAL,
        layout1: "orders",
        layout2: "timeline"
    };
    private _displayMode: OrderPlanningSplitViewDisplayMode;
    public displayModes = [
        OrderPlanningSplitView.ORDERS_EQUIPMENT_HORIZONTAL,
        OrderPlanningSplitView.ORDERS_EQUIPMENT_VERTICAL,
        OrderPlanningSplitView.ORDERS_TIMELINE_VERTICAL
    ];
    private _layoutTimeline: OrderPlanningTimeline;

    override onLoad() {
        this.layoutOrderPlanningOrders.layoutOPAvailTools.toolPanel.setSplitViewLayout(this);
        this.layoutOrderPlanningEquipment.layoutOPEquipTools.toolPanel.setSplitViewLayout(this);
        this.layoutOrderPlanningOrders.tableAvailableMoves.removeColumn("columnHeaderDeadhead");
        this.layoutOrderPlanningOrders.tableAvailableMoves.removeColumn("columnHeaderVelocity");
        const deadheadColumn = this.layoutOrderPlanningEquipment.tableEquipMoves.getColumnByCaption("Deadhead");
        this.layoutOrderPlanningEquipment.tableEquipMoves.setColumnVisibility(deadheadColumn, false);
        this.splitterPlanning.firstComponent.style.overflowX = "hidden";
        this.splitterPlanning.secondComponent.style.overflowX = "hidden";
        this.layoutOrderPlanningEquipment.layoutPlanningTitle.attachToSplitter(this.splitterPlanning);
        this.displayMode = OrderPlanningSplitView.ORDERS_EQUIPMENT_HORIZONTAL;
        this.layoutOrderPlanningOrders.tableAvailableMoves.removeColumn("columnProratedRevenue");
        this.layoutOrderPlanningOrders.tableAvailableMoves.removeColumn("columnRevenuePerDistance");
        this.layoutOrderPlanningOrders.tableAvailableMoves.removeColumn("columnMatchPercent");
    }

    // Called from MultiProfilePlanningTabs.ts
    async loadProfile(value?: string | ModelRow) {
        Promise.all([
            this.layoutOrderPlanningOrders?.loadProfile(value),
            this.layoutOrderPlanningEquipment?.loadProfile(value),
            this.layoutOrderPlanningEquipment?.setTitles(),
            this.layoutTimeline?.loadProfile(value)
        ]).then(() => {
            const defaultView = this.layoutOrderPlanningOrders?.profile.get("default_view", "V");
            this.displayMode = this.displayModes.find(mode => mode.dbValue === defaultView);
            const parentLayout = this.getParentLayout();
            if (parentLayout instanceof OperationsManagementMultiProfile) {
                const image = "C" === defaultView ? parentLayout.imageOrdersTimelineVertical
                    : "H" === defaultView ? parentLayout.imageOrdersEquipmentVertical
                        : parentLayout.imageOrdersEquipmentHorizontal;
                const isActiveTab = parentLayout.tabsetOrderPlanningProfiles.getActiveTab().components.find(
                    (component: Component) => component instanceof OrderPlanningSplitView
                )?.displayMode.dbValue === this._displayMode.dbValue;
                if (isActiveTab) parentLayout.updateImageColors(image)
            }
            WindowTitle.refresh("OrderPlanningSplitView");
        });
    }

    get displayMode(): OrderPlanningSplitViewDisplayMode {
        return this._displayMode;
    }

    set displayMode(value: OrderPlanningSplitViewDisplayMode) {
        this._displayMode = value;
        this.splitterPlanning.orientation = value.splitterOrientation;
        this.restoreSplitterPosition();
        this.resolveLayout(value.layout1).then(layout => this.splitterPlanning.firstComponent = layout);
        this.resolveLayout(value.layout2).then(layout => {
            this.splitterPlanning.secondComponent = layout;
            if (this.displayMode == OrderPlanningSplitView.ORDERS_TIMELINE_VERTICAL) {
                this.layoutTimeline.initialCellLoaded = false;
                this.layoutTimeline.tableTimeline?.applyLastScrollLeft();
            } else if (this.displayMode == OrderPlanningSplitView.ORDERS_EQUIPMENT_VERTICAL) {
                this.layoutOrderPlanningEquipment.tableEquipMoves?.applyLastScrollLeft();
            }
            const tableRows = this.layoutOrderPlanningOrders.tableAvailableMoves?.selectedRows;
            if (tableRows != null && tableRows.length > 0) {
                const toolPanel = this.layoutOrderPlanningOrders.getToolsPanel();
                const feasibilityAction = toolPanel.getVisibleAction(Tools.FEASIBILITY);
                if (feasibilityAction != null) {
                    toolPanel.syncButtonState(feasibilityAction, tableRows);
                }
            }
        });
    }

    getActiveEquipLayout(): OrderPlanningEquipment | OrderPlanningTimeline {
        return this.splitterPlanning.secondComponent as OrderPlanningEquipment | OrderPlanningTimeline;
    }

    get layoutTimeline(): OrderPlanningTimeline {
        return this._layoutTimeline;
    }

    private async resolveLayout(layoutKey: string): Promise<Layout> {
        switch (layoutKey) {
            case "orders":
                return this.layoutOrderPlanningOrders;
            case "equipment":
                return this.layoutOrderPlanningEquipment;
            case "timeline":
                if (this._layoutTimeline != null)
                    return this._layoutTimeline;
                const layoutPath = "lme/dispatch/planning/timeline/OrderPlanningTimeline";
                const findNearOn = this.layoutOrderPlanningOrders.layoutOPAvailTools.toolPanel.findNearOn();
                this._layoutTimeline = Layout.getLayout(layoutPath, { isNested: true }) as OrderPlanningTimeline;
                if (findNearOn) {
                    const selectedRow = this.layoutOrderPlanningOrders.tableAvailableMoves?.selectedRow;
                    const fn_move_id = selectedRow?.data.get("movement_id");
                    const fn_orig_latitude = selectedRow?.data.get("origin_latitude");
                    const fn_orig_longitude = selectedRow?.data.get("origin_longitude");
                    const fn_orig_city = selectedRow?.data.get("origin_city");
                    const fn_orig_state = selectedRow?.data.get("origin_state");
                    const fn_orig_zip = selectedRow?.data.get("origin_zip");
                    this._layoutTimeline.findNearProps = {
                        fn_move_id,
                        fn_orig_latitude,
                        fn_orig_longitude,
                        fn_orig_city,
                        fn_orig_state,
                        fn_orig_zip
                    }
                }
                this._layoutTimeline.addLayoutLoadListener(async () => {
                    await this._layoutTimeline.loadProfile(this.layoutOrderPlanningOrders.profile);
                    const columnDeadhead = this._layoutTimeline.tableTimeline.getColumnByCaption("Deadhead");
                    columnDeadhead.headingCell.getSortSelectorLabel().color = "success";
                    columnDeadhead.headingCell.getSortSelectorLabel().style.fontWeight = "600";
                    this._layoutTimeline.tableTimeline.setColumnVisibility(columnDeadhead, findNearOn);
                    this._layoutTimeline.layoutPlanningTitle.attachToSplitter(this.splitterPlanning);
                    this._layoutTimeline.layoutOPTimelineTools.toolPanel.setSplitViewLayout(this);
                });
                return this._layoutTimeline;
            default:
                break;
        }
    }

    splitterPlanningOnChange() {
        this.saveSplitterPositionDebounced();
    }

    private saveSplitterPositionDebounced = DebouncedFunction.create(() => this.saveSplitterPosition(), 500);

    private saveSplitterPosition() {
        const positionsObject = this.getSplitterPositionsFromLocalStorage();
        positionsObject[this.displayMode.name] = this.splitterPlanning.positionAsPercent;
        LocalUserSettings.set(OrderPlanningSplitView.LOCAL_SETTINGS_SPLITTER_KEY, positionsObject);
    }

    private restoreSplitterPosition() {
        const positionsObject = this.getSplitterPositionsFromLocalStorage();
        const value = positionsObject[this.displayMode.name];
        if (value != null)
            this.splitterPlanning.position = value;
    }

    private getSplitterPositionsFromLocalStorage(): any {
        const positionsObject = LocalUserSettings.getObject(OrderPlanningSplitView.LOCAL_SETTINGS_SPLITTER_KEY);
        return positionsObject != null ? positionsObject : {};
    }

    override get windowTitle(): string {
        return this.layoutOrderPlanningOrders?.profile?.getString("id") || "Operations Management";
    }

    public get profileRow(): ModelRow {
        return this.layoutOrderPlanningOrders?.profile;
    }
}
